/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import apiClient from '../apiClient';
import {
    StoreDetailsDto,
    StoreCashboxesListDto,
    StoreDetailsForMap,
    StoreOnMapResource,
    StoresMapSearchParams,
    UpdatedStoreDetailsDto,
    StoreCoordinatesHistoryDto,
    StorePhotoInfoDto,
    DeleteStorePhotoDto,
    S3Buckets,
    SetStorePhotoOnPreviewDto,
    StoreInspectionsHistoryDto,
    StoreInspectionDto,
    InspectionStatus,
    StoresListDto,
    StoresListExcelRequestParams,
    StoresListRequestParams,
    StoresForMap,
    StoreCashBoxesListQueryParams,
} from './storesClientTypings';
import {
    cutEmptyParams,
    cutEmptyParamsIncludingArrays,
    DATA_UNAVAILABLE,
    makeArrayFromCommaSeparatedString,
} from '../../../utils/utils';
import {
    RegistryTurnoversByPeriodDto,
    RegistryTurnoversByCashBoxesDto,
    TurnoversByPeriodQueryParams,
    TurnoversByCashBoxesQueryParams,
    TurnoversByPeriodEXCELParams,
    TurnoversByTaxEntityEXCELParams,
} from '../apiCommon.typings';
import { StoresListTableData } from '../../../components/RegistryWidgets/Stores/StoresList/StoresListConfig';
import { RequestToCreateExcelDto } from '../downloadings/downloadingsClient.typings';
import { CashBoxesStats } from '../cashBoxes/cashBoxesClient.typings';

const SEARCH_TYPE_TO_SEARCH_PARAM: { [key in keyof StoresListTableData]?: string } = {
    taxNumber: 'taxNumbers',
    companyTaxNumber: 'companyTaxNumbers',
    name: 'names',
};

const getSearchParamName = (searchType: keyof StoresListTableData): string => {
    let searchParam = SEARCH_TYPE_TO_SEARCH_PARAM[searchType];
    if (searchParam === undefined) searchParam = DATA_UNAVAILABLE;
    return searchParam;
};

const adjustStoresListFilterParams = (params: StoresListRequestParams | StoresListExcelRequestParams) => {
    const { search_type, search_text } = params;
    const queryParams = {
        ...params,
        [getSearchParamName(search_type as keyof StoresListTableData)]: makeArrayFromCommaSeparatedString(
            search_text,
            true,
        ),
    };
    delete queryParams.search_text;
    delete queryParams.search_type;

    return cutEmptyParamsIncludingArrays(queryParams);
};

async function listStores(params: StoresListRequestParams): Promise<StoresListDto> {
    return apiClient.post<StoresListDto>('1.0.0/stores', adjustStoresListFilterParams(params));
}

async function downloadStoresListAsExcel(params: StoresListExcelRequestParams): Promise<RequestToCreateExcelDto> {
    return apiClient.post<RequestToCreateExcelDto>('1.0.0/stores/excel', adjustStoresListFilterParams(params));
}

async function getStore(taxNumber: string): Promise<StoreDetailsDto> {
    return apiClient.get<StoreDetailsDto>(`1.0.0/stores/${taxNumber}`);
}

async function updateStoreInfo(data: StoreOnMapResource): Promise<UpdatedStoreDetailsDto> {
    const { taxNumber, latitude: lat, longitude: lng } = data;
    return apiClient.patch<UpdatedStoreDetailsDto>(`1.0.0/stores/${taxNumber}`, { lat, lng });
}

async function getStoreCashboxes(payload: {
    taxNumber: string;
    params: StoreCashBoxesListQueryParams;
}): Promise<StoreCashboxesListDto> {
    return apiClient.get<StoreCashboxesListDto>(`1.0.0/stores/${payload.taxNumber}/cashboxes`, payload.params);
}

async function getCoordinatesHistory(taxNumber: string): Promise<StoreCoordinatesHistoryDto> {
    return apiClient.get<StoreCoordinatesHistoryDto>(`1.0.0/stores/${taxNumber}/coordinatesHistory`);
}

async function downloadCoordinatesHistoryAsExcel(taxNumber: string): Promise<ArrayBuffer> {
    return apiClient.downloadFile(`1.0.0/stores/${taxNumber}/coordinatesHistory/excel`);
}

async function getInspectionsHistory(taxNumber: string): Promise<StoreInspectionsHistoryDto> {
    return apiClient.get<StoreInspectionsHistoryDto>(`1.0.0/stores/${taxNumber}/inspections`);
}

async function createInspection(taxNumber: string, comment: string): Promise<StoreInspectionDto> {
    return apiClient.post<StoreInspectionDto>(`1.0.0/stores/${taxNumber}/inspections`, { initiatorComment: comment });
}

async function cancelInspection(taxNumber: string, inspectionId: string): Promise<{}> {
    return apiClient.delete<{}>(`1.0.0/stores/${taxNumber}/inspections/${inspectionId}`);
}

async function assignInspection(taxNumber: string, inspectionId: string): Promise<StoreInspectionDto> {
    return apiClient.patch<StoreInspectionDto>(`1.0.0/stores/${taxNumber}/inspections/${inspectionId}/assign`, {
        status: InspectionStatus.IN_PROGRESS,
    });
}

async function completeInspection(
    taxNumber: string,
    completeInspectionData: Partial<StoreInspectionDto>,
): Promise<StoreInspectionDto> {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { inspectionId, ...body } = completeInspectionData;
    return apiClient.patch<StoreInspectionDto>(
        `1.0.0/stores/${taxNumber}/inspections/${completeInspectionData.inspectionId}/complete`,
        { status: InspectionStatus.CLOSED, ...body },
    );
}

function getStoresForMap(params: StoresMapSearchParams): Promise<StoresForMap> {
    const { search_type, search_text, afterKeyId, limit, city, storeTypes, storeStatuses } = params.filterParams;
    const { viewport } = params;
    const { bounds, zoom } = viewport;

    delete params.filterParams;

    const body = {
        bounds,
        zoom,
        afterKeyId,
        limit,
        cityNames: city ? [city.name] : null,
        storeTypes,
        storeStatuses,
        [search_type]: makeArrayFromCommaSeparatedString(search_text, true),
    };

    return apiClient.post<StoresForMap>('1.0.0/map/stores', cutEmptyParamsIncludingArrays(body));
}

function getStoreDetailsForMap(taxNumber: string): Promise<StoreDetailsForMap> {
    return apiClient.get<StoreDetailsForMap>(`1.0.0/map/stores/${taxNumber}`);
}

async function getStoreCashBoxesStats(taxNumber: string): Promise<CashBoxesStats> {
    return apiClient.get<CashBoxesStats>(`1.0.0/stores/${taxNumber}/cashboxes/stats`);
}

async function getStoreTurnoversByPeriod(
    taxNumber: string,
    params: TurnoversByPeriodQueryParams,
): Promise<RegistryTurnoversByPeriodDto[]> {
    const { ...newParams } = params;
    delete newParams.group;
    return apiClient.get<RegistryTurnoversByPeriodDto[]>(
        `1.0.0/stores/${taxNumber}/turnovers`,
        cutEmptyParams(newParams),
    );
}

async function getStoreTurnoversByCashBoxes(
    taxNumber: string,
    params: TurnoversByCashBoxesQueryParams,
): Promise<RegistryTurnoversByCashBoxesDto> {
    return apiClient.get<RegistryTurnoversByCashBoxesDto>(
        `1.0.0/stores/${taxNumber}/cashboxes/turnovers`,
        cutEmptyParams(params),
    );
}

async function downloadStoreTurnoversByPeriodAsExcel(
    taxNumber: string,
    params: TurnoversByPeriodEXCELParams,
): Promise<ArrayBuffer> {
    return apiClient.downloadFile(`1.0.0/stores/${taxNumber}/turnovers/excel`, cutEmptyParams(params));
}

async function downloadStoreTurnoversByCashBoxesAsExcel(
    taxNumber: string,
    params: TurnoversByTaxEntityEXCELParams,
): Promise<ArrayBuffer> {
    return apiClient.downloadFile(`1.0.0/stores/${taxNumber}/cashboxes/turnovers/excel`, cutEmptyParams(params));
}

async function getStorePhotosInfo(taxNumber: string, bucketName: S3Buckets): Promise<StorePhotoInfoDto[]> {
    return apiClient.get<StorePhotoInfoDto[]>(`1.0.0/stores/${taxNumber}/photos/${bucketName}`);
}

async function uploadStorePhoto(
    taxNumber: string,
    bucketName: S3Buckets,
    originalFileName: string,
    inspectionId?: string,
): Promise<StorePhotoInfoDto[]> {
    return apiClient.post<StorePhotoInfoDto[]>(
        `1.0.0/stores/${taxNumber}/photo/${bucketName}/${originalFileName}${inspectionId ? `/${inspectionId}` : ''}`,
    );
}

async function setPreviewOrSubmit(taxNumber: string, body: SetStorePhotoOnPreviewDto[]): Promise<{}> {
    return apiClient.patch<{}>(`1.0.0/stores/${taxNumber}/photos`, body);
}

async function deleteStorePhoto(
    taxNumber: string,
    names: string[],
    bucketName: S3Buckets,
    addedPhotoUserId?: number,
): Promise<DeleteStorePhotoDto> {
    return apiClient.delete<DeleteStorePhotoDto>(`1.0.0/stores/${taxNumber}/photos`, {
        addedPhotoUserId,
        names,
        bucketName,
    });
}

export const storesClient = {
    listStores,
    getStore,
    updateStoreInfo,
    getCoordinatesHistory,
    getInspectionsHistory,
    createInspection,
    cancelInspection,
    assignInspection,
    completeInspection,
    downloadCoordinatesHistoryAsExcel,
    getStoresForMap,
    downloadStoresListAsExcel,
    getStoreDetailsForMap,
    getStoreCashBoxesStats,
    getStoreTurnoversByPeriod,
    getStoreTurnoversByCashBoxes,
    downloadStoreTurnoversByPeriodAsExcel,
    downloadStoreTurnoversByCashBoxesAsExcel,
    getStorePhotosInfo,
    setPreviewOrSubmit,
    deleteStorePhoto,
    uploadStorePhoto,
    getStoreCashboxes,
};
